/**
 * @file OrderShipmentsList.tsx
 * @description Список отгрузок заказа с действиями (phase19.12 шаг 5).
 *
 * Разводка «действие → id отгрузки»: сама вкладка занимается загрузкой данных
 * и тостами, а список — только отрисовкой.
 */

import type { PrintingState } from "./useShipmentActions";
import { OrderShipmentCard } from "./OrderShipmentCard";
import { EditorialCard } from "../ui/EditorialCard";
import { useFocusedRow } from "../../hooks/useFocusedRow";
import type { ShipmentPrintForm } from "../../lib/api-carriers-sdek";
import type { OrderDeliveryDto, ShipmentDto } from "../../lib/api-orders";

type ShipmentHandler = (shipmentId: string) => void;

interface Props {
  items: ShipmentDto[];
  canManage: boolean;
  /** По заказу возвращали деньги — предупреждаем на черновиках отгрузок. */
  orderRefunded?: boolean;
  acting: boolean;
  /** Какая печатная форма и у какой отгрузки сейчас готовится. */
  printing: PrintingState | null;
  /** Позиция доставки заказа — источник цены «клиенту». */
  delivery: OrderDeliveryDto | null;
  onQuote: ShipmentHandler;
  onPickPoint: ShipmentHandler;
  onSendToSdek: ShipmentHandler;
  onSyncSdek: ShipmentHandler;
  onPrint: (shipmentId: string, form: ShipmentPrintForm) => void;
  onShip: ShipmentHandler;
  onDeliver: ShipmentHandler;
  /** Удаление черновика: `onDone` вызывается после запроса (закрыть диалог). */
  onDelete: (shipmentId: string, onDone: () => void) => void;
  /** Ручной трек-номер отгрузки (phase19.15). */
  onSaveTrack: (shipmentId: string, trackNumber: string) => void;
  /**
   * id отгрузки из `?focus=`: у отгрузки нет своей страницы, поэтому узел
   * «Пути заказа» приводит сюда и обводит нужную карточку (phase19.24 §4).
   */
  focusId?: string | null;
}

export function OrderShipmentsList({
  items,
  canManage,
  orderRefunded = false,
  acting,
  printing,
  delivery,
  onQuote,
  onPickPoint,
  onSendToSdek,
  onSyncSdek,
  onPrint,
  onShip,
  onDeliver,
  onDelete,
  onSaveTrack,
  focusId,
}: Props) {
  const focused = useFocusedRow(focusId);
  if (items.length === 0) {
    return (
      <EditorialCard>
        <EditorialCard.Body>
          <div className="py-8 text-center text-on-surface-variant">Отгрузок пока нет.</div>
        </EditorialCard.Body>
      </EditorialCard>
    );
  }

  const item = delivery?.deliveryItem ?? null;

  return (
    <>
      {items.map((s) => (
        <div
          key={s.id}
          {...focused.rowAttr(s.id)}
          className={
            focused.highlightedId === s.id ? "rounded-xl ring-2 ring-primary ring-offset-2" : undefined
          }
        >
        <OrderShipmentCard
          shipment={s}
          canManage={canManage}
          orderRefunded={orderRefunded}
          acting={acting}
          printing={printing?.shipmentId === s.id ? printing.form : null}
          // Цену «клиенту» показываем, только если отгрузка едет по тому же
          // тарифу, что оплатил клиент, — иначе это разные числа.
          clientPrice={item && item.tariffCode === s.tariffCode ? item.priceGross : null}
          onQuote={() => onQuote(s.id)}
          onPickPoint={() => onPickPoint(s.id)}
          onSendToSdek={() => onSendToSdek(s.id)}
          onSyncSdek={() => onSyncSdek(s.id)}
          onPrint={(form) => onPrint(s.id, form)}
          onShip={() => onShip(s.id)}
          onDeliver={() => onDeliver(s.id)}
          onDelete={(onDone) => onDelete(s.id, onDone)}
          onSaveTrack={(track) => onSaveTrack(s.id, track)}
        />
        </div>
      ))}
    </>
  );
}
